// Configuration for SharePoint OAuth and API
// Values are loaded from API config first, then from environment variables

// Module-level variable to store API config settings
let apiConfigSettings = null;

/**
 * Initialize configuration from API config
 * This should be called once during app initialization
 * @param {Object} apiConfig - The configuration object from the API
 */
export const initializeConfig = (apiConfig) => {
  if (apiConfig && apiConfig.settings) {
    apiConfigSettings = apiConfig.settings;
    console.log('Config initialized from API:', Object.keys(apiConfigSettings));
  }
};

// Mapping between config.js keys (UPPERCASE) and API settings keys (camelCase)
const keyMapping = {
  'CLIENT_ID': 'clientId',
  'TENANT_ID': 'tenantId',
  'CLIENT_SECRET': 'clientSecret',
  'SHAREPOINT_SITE_URL': 'sharePointSiteUrl',
  'SHAREPOINT_LIBRARY_NAME': 'sharePointLibraryName',
  'SHAREPOINT_SITE_ID': 'sharePointSiteId',
  'SHAREPOINT_LIST_ID': 'sharePointListId',
  'DATAVERSE_BASE_URL': 'dataverseBaseUrl',
  'DATAVERSE_ENTITY_SET': 'dataverseEntitySet',
  'DATAVERSE_SCOPE': 'dataverseScope',
  'CORS_PROXY': 'corsProxy',
  'BACKEND_URL': 'backendUrl',
  'ADMIN_USERNAME': 'adminUsername',
  'ADMIN_PASSWORD': 'adminPassword',
};

// Helper function to get configuration value
// Priority: API config (runtime) > environment variables (build-time)
const getConfig = (key, defaultValue = '') => {
  // First check API config with mapped key
  const mappedKey = keyMapping[key] || key;
  if (apiConfigSettings && apiConfigSettings[mappedKey] !== undefined && apiConfigSettings[mappedKey] !== '') {
    return apiConfigSettings[mappedKey];
  }
  // Also check with original key (for camelCase direct access)
  if (apiConfigSettings && apiConfigSettings[key] !== undefined && apiConfigSettings[key] !== '') {
    return apiConfigSettings[key];
  }
  // Fall back to environment variables
  return process.env[`REACT_APP_${key}`] || defaultValue;
};

export const msalConfig = {
  auth: {
    get clientId() {
      return getConfig('CLIENT_ID', '');
    },
    // Use 'organizations' as fallback to restrict to organizational accounts only
    // This prevents authentication from personal Microsoft accounts
    get authority() {
      return `https://login.microsoftonline.com/${getConfig('TENANT_ID', 'organizations')}`;
    },
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

export const loginRequest = {
  scopes: ['https://graph.microsoft.com/.default'],
};

export const sharePointConfig = {
  get siteUrl() {
    return getConfig('SHAREPOINT_SITE_URL', '');
  },
  get libraryName() {
    return getConfig('SHAREPOINT_LIBRARY_NAME', 'Documents');
  },
  // SharePoint site and list identifiers - these can be configured via environment variables
  get siteId() {
    return getConfig('SHAREPOINT_SITE_ID', 'firecosrl682.sharepoint.com,da61118c-2930-4473-842e-dd0458780aa0,5c37dfac-e3b3-4dec-ac82-b75ca62ced19');
  },
  get listId() {
    return getConfig('SHAREPOINT_LIST_ID', 'd9e67650-f844-43df-8b9d-0b7b80ea1a49');
  },
};

// OAuth credentials for client credentials flow
// IMPORTANT: Set these values via environment variables - do not hardcode secrets
// Using getters to ensure values are evaluated at runtime after initializeConfig() is called
export const oauthConfig = {
  get clientId() {
    return getConfig('CLIENT_ID', '');
  },
  get tenantId() {
    return getConfig('TENANT_ID', '');
  },
  get clientSecret() {
    return getConfig('CLIENT_SECRET', '');
  },
};

export const adminConfig = {
  get username() {
    return getConfig('ADMIN_USERNAME', '');
  },
  get password() {
    return getConfig('ADMIN_PASSWORD', '');
  }
};

export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
  baseUrl: 'https://graph.microsoft.com/v1.0',
};

// Dataverse configuration for querying matricola data
export const dataverseConfig = {
  get baseUrl() {
    return getConfig('DATAVERSE_BASE_URL', 'https://orgc0465cb6.crm4.dynamics.com');
  },
  get entitySet() {
    return getConfig('DATAVERSE_ENTITY_SET', 'cr2a3_lotti_docs');
  },
  get scope() {
    return getConfig('DATAVERSE_SCOPE', 'https://orgc0465cb6.crm4.dynamics.com/.default');
  },
};

// CORS proxy configuration
export const corsConfig = {
  get proxyUrl() {
    return getConfig('CORS_PROXY', 'https://corsproxy.io/?');
  },
};

// Backend API configuration
// Automatically detects environment:
// - localhost: uses http://localhost:8080 (or REACT_APP_BACKEND_URL if set)
// - production: uses relative paths (same origin)
export const backendConfig = {
  get apiUrl() {
    const configuredUrl = getConfig('BACKEND_URL', '');
    
    // If explicitly configured, use that
    if (configuredUrl) {
      return configuredUrl;
    }
    
    // Auto-detect: localhost uses local backend, production uses relative paths
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      return 'http://localhost:8080';
    }
    
    // Production: use relative paths (same origin on Azure)
    return '';
  },
  isEnabled: () => {
    // Always enabled - uses backend on localhost, relative paths on production
    return true;
  }
};
